import React, { useEffect } from 'react';
import { Trans } from '@lingui/macro';
import { Typography } from '@material-ui/core';
import { ShowStateModel } from '../MainGraph';
import { GraphActuationsType, MainGraphDataModel, PMZoneSiteModel } from '@shared/models';
import { CustomCheckbox } from '@hwm/ui-lib';
import { CustomChannelNamesFn, mainGraphChannelLabelAndColor } from '../Graph/Graph.utils';

const actuationTailwindClass = 'w-5 h-4 mx-0.5 rounded-sm';
interface GraphLegendProps {
  showState: ShowStateModel;
  setShowState: React.Dispatch<React.SetStateAction<ShowStateModel>>;
  graphData: MainGraphDataModel[];
  sitesData: PMZoneSiteModel[];
  isPrinting: boolean;
  solenoidActuationThreshold?: number;
  /**
   * `dataTestId` - name of a Graph Legend (preferably `<graph_name>-legend`)
   * @example
   * ```tsx
   * <GraphLegend {...props} dataTestId="test-graph-legend" />
   */
  dataTestId: string;
}

export const GraphLegend: React.FC<GraphLegendProps> = ({
  showState,
  setShowState,
  graphData,
  sitesData,
  isPrinting,
  solenoidActuationThreshold,
  dataTestId,
}) => {
  let colorIterator = 0;

  useEffect(() => {
    if (!showState.empty && graphData) {
      let showChannels: boolean[][] = [];
      graphData.forEach((gd) => {
        const show: boolean[] = [];
        gd.channelData?.forEach(() => show.push(true));
        showChannels.push(show);
      });
      setShowState((p) => ({ ...p, channels: showChannels }));
    }
  }, [graphData, showState.empty, setShowState]);

  return (
    <div className="flex flex-col" data-testid={`${dataTestId}-wrapper`}>
      <Typography variant="caption" data-testid={`${dataTestId}-time-gmt`}>
        <Trans>Display time in GMT</Trans>
      </Typography>
      <div
        className={`flex ${isPrinting ? 'flex-row flex-wrap' : 'flex-col'} mt-2 mb-1`}
        data-testid={`${dataTestId}-sites-channels`}
      >
        {graphData?.map((gd, i) =>
          gd.channelData.map(({ channelSummary }, j) => {
            const { channelNo, channelName } = channelSummary;
            const siteInfo = sitesData?.find((site) => site.id === gd.siteSummary.siteId);
            const isPrv = siteInfo?.siteType === 'PRV';

            const { label, color } = mainGraphChannelLabelAndColor(
              gd.siteSummary?.primaryAddress ?? '',
              channelSummary,
              colorIterator,
              isPrv
            );

            colorIterator = colorIterator + (isPrv && channelNo <= CustomChannelNamesFn().length ? 0 : 1);

            return (
              <CustomCheckbox
                key={channelName + i + j}
                label={label}
                checked={Boolean(showState.channels?.[i]?.[j])}
                onChange={(_, check) => {
                  const newState = [...showState.channels];
                  newState[i][j] = check;
                  setShowState((p) => ({ ...p, channels: newState }));
                }}
                customColor={color ?? null}
                wrapperClasses={`pr-2 mb-2 ${isPrinting ? 'w-72' : ''}`}
                dataTestId={`${dataTestId}-site-${i}-channel-${j}`}
              />
            );
          })
        )}
      </div>
      {showState?.actuations ? (
        <div
          className={`flex ${isPrinting ? 'flex-row flex-wrap' : 'flex-col'} mb-2`}
          data-testid={`${dataTestId}-actuations`}
        >
          {Object.entries(showState.actuations)
            .filter(([, { state }]) => Boolean(state))
            .map(([key, { label, color }]) => {
              return (
                <div
                  key={key}
                  className={`flex items-center ${isPrinting ? 'w-72' : ''}`}
                  data-testid={`${dataTestId}-actuation-${key.toLowerCase()}`}
                >
                  <div className={actuationTailwindClass} style={{ background: color }} />
                  <Typography
                    style={{ marginLeft: 4 }}
                    data-testid={`${dataTestId}-actuation-${key.toLowerCase()}-label`}
                  >
                    {label}
                    {key === GraphActuationsType.Recommended ? ` ${solenoidActuationThreshold ?? ''}` : null}
                  </Typography>
                </div>
              );
            })}
        </div>
      ) : null}
    </div>
  );
};
